<template>
  <div class="award-checkout">
    <award-checkout-form
      v-if="!submitted"
      :additional-classes="additionalClasses"
      :contact-fields="contactFields"
      :has-insufficient-points="hasInsufficientPoints"
      @submit="submitForm"
    ></award-checkout-form>
    <award-checkout-confirmation
      v-else
      :list-view="listView"
      :checkout-data="checkoutData"
      :salutations="salutations"
      :countries="countries"
      :additional-classes="additionalClasses"
    ></award-checkout-confirmation>
    <notification-modal
      v-if="error"
      type="error"
      :title="translate('lblErrorTitle')"
      :subtitle="error.subtitle"
      :content="error.content"
      :redirect-url="error.redirectUrl"
      :prevent-close="true"
    ></notification-modal>
  </div>
</template>

<script>
import AwardCheckoutForm from './components/AwardCheckoutForm.vue';
import AwardCheckoutConfirmation from './components/AwardCheckoutConfirmation.vue';
import NotificationModal from '../shared/components/NotificationModal.vue';
import LoyaltyApiConnectorMixin from './mixins/LoyaltyApiConnectorMixin';

export default {
  name: 'AwardCheckout',
  data: () => ({
    sessionStorageKey: 'loyaltyCheckoutData',
    submitted: false,
    error: null,
    checkoutData: null,
    points: 0,
    hasInsufficientPoints: false,
  }),
  props: {
    translations: Object,
    contactFields: Array,
    listView: String,
    additionalClasses: String,
  },
  mixins: [LoyaltyApiConnectorMixin],
  computed: {
    salutations() {
      return this.fieldOptionsToDictionary('salutation');
    },
    countries() {
      return this.fieldOptionsToDictionary('country');
    },
  },
  methods: {
    getCookieValue(name) {
      const value = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
      return value ? value.pop() : '';
    },
    fieldOptionsToDictionary(fieldName) {
      return this.contactFields
        .flat()
        .find((field) => field.name === fieldName)
        .options.reduce((obj, option) => {
          // eslint-disable-next-line no-param-reassign
          obj[option.value] = option.label;
          return obj;
        }, {});
    },
    async submitForm(formData) {
      const requestPayload = this.checkoutData.awards.map((award) => ({
        ...formData.shippingData,
        entity_id: award.entity_id,
        article_number: award.article_number,
        quantity: award.quantity,
      }));
      await this.$http({
        method: 'post',
        url: `${this.apiUrl}/award-orders`,
        data: requestPayload,
        headers: {
          Authorization: `Bearer ${this.getCookieValue('Authorization')}`,
          'Content-Type': 'application/json',
        },
      })
        .then(() => {
          this.submitted = true;
          this.checkoutData = {
            ...this.checkoutData,
            ...formData,
          };
          window.sessionStorage.removeItem('loyaltyCheckoutData');
          window.sessionStorage.removeItem('selectedAwardItems');
        })
        .catch((error) => {
          const message = {
            subtitle: this.translate('lblApiErrorSubtitle'),
            redirectUrl: this.listView,
          };
          if (error.response?.message) {
            message.content = error.response.message;
          }
          if (error.response?.data?.message) {
            message.content = error.response.data.message;
          }
          this.error = message;
        });
    },
  },
  async created() {
    if (this.translations) {
      this.addDictionary(this.translations);
    }
    const jsonString = window.sessionStorage.getItem(this.sessionStorageKey);
    if (jsonString !== null) {
      this.checkoutData = JSON.parse(jsonString);
    }
    if (!jsonString || !this.checkoutData?.awards?.length) {
      this.error = {
        subtitle: this.translate('lblDataErrorSubtitle'),
        content: this.translate('lblDataErrorContent'),
        redirectUrl: this.listView,
      };
    }
    const result = await this.getUserApiResult();
    if (result.point_summary) {
      this.points = result.point_summary.point_sum;
      const sum = this.checkoutData.awards.reduce((accumulator, currentValue) => (accumulator + (currentValue.quantity * currentValue.points)), 0);
      this.hasInsufficientPoints = sum > this.points;
    }
  },
  components: {
    NotificationModal,
    AwardCheckoutForm,
    AwardCheckoutConfirmation,
  },
};
</script>

<style lang="scss">
.award-checkout {
  .buttons {
    display: flex;

    @media (max-width: rem(568px)) {
      flex-wrap: wrap;
    }

    .button {
      @media (max-width: rem(568px)) {
        width: 100%;
      }

      &:not(:first-child) {
        margin-left: rem(24px);

        @media (max-width: rem(568px)) {
          margin-left: 0;
        }
      }
    }
  }
}
</style>
